'use client';

import { FC, useState } from 'react';
import { Play } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AnnouncementVideoProps {
  videoUrl: string;
  posterUrl?: string | null;
  className?: string;
  /** Autoplay immediately instead of showing the poster/play overlay (used in the lightbox). */
  autoStart?: boolean;
}

/**
 * Extract a YouTube video id from the common URL shapes (watch?v=, youtu.be/, /embed/, /shorts/).
 * Returns null for anything that isn't YouTube — those are treated as a self-hosted mp4.
 */
export function getYouTubeId(url: string): string | null {
  const u = (url || '').trim();
  if (!/youtube\.com|youtu\.be/i.test(u)) return null;
  const patterns = [
    /[?&]v=([\w-]{11})/, // watch?v=ID
    /youtu\.be\/([\w-]{11})/, // youtu.be/ID
    /\/embed\/([\w-]{11})/, // /embed/ID
    /\/shorts\/([\w-]{11})/, // /shorts/ID
  ];
  for (const re of patterns) {
    const m = u.match(re);
    if (m) return m[1];
  }
  return null;
}

/**
 * Announcement video player. Self-hosted mp4 (CSP allows assets/media.marketbetter.ai) renders via a
 * native <video>; a YouTube link renders via the privacy-preserving youtube-nocookie.com embed (CSP
 * frame-src allows that host). Both show a poster frame with a play overlay; on click (or autoStart)
 * the media swaps in and autoplays.
 */
const AnnouncementVideo: FC<AnnouncementVideoProps> = ({ videoUrl, posterUrl, className, autoStart }) => {
  const poster = (posterUrl || '').trim();
  const [started, setStarted] = useState(autoStart || !poster);
  const youTubeId = getYouTubeId(videoUrl);

  const renderMedia = () => {
    if (youTubeId) {
      // Lightbox / no-gesture mounts (autoStart) must be muted for reliable autoplay; an inline
      // poster-click is a direct gesture so it can play with sound.
      const params = new URLSearchParams({
        autoplay: '1',
        rel: '0',
        modestbranding: '1',
        ...(autoStart ? { mute: '1' } : {}),
      });
      return (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${youTubeId}?${params.toString()}`}
          title="Announcement video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 h-full w-full border-0"
        />
      );
    }
    return (
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <video
        src={videoUrl}
        poster={poster || undefined}
        controls
        autoPlay
        // Lightbox (autoStart) mounts from a state change, not synchronously in the click — start
        // muted so browsers reliably autoplay (the user unmutes via controls). Inline play is a
        // direct user gesture on the poster overlay, so it can autoplay with sound.
        muted={!!autoStart}
        controlsList="nodownload"
        playsInline
        className="absolute inset-0 h-full w-full"
      />
    );
  };

  return (
    <div className={cn('relative w-full overflow-hidden rounded-xl border border-slate-200 bg-black', className)}>
      <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
        {started ? renderMedia() : (
          <button
            type="button"
            onClick={() => setStarted(true)}
            aria-label="Play video"
            className="absolute inset-0 flex h-full w-full items-center justify-center bg-cover bg-center"
            style={poster ? { backgroundImage: `url(${poster})` } : { background: '#0f172a' }}
          >
            <span className="absolute inset-0 bg-black/30" />
            <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-white/95 shadow-lg">
              <Play className="ml-0.5 h-6 w-6 text-blue-700" fill="currentColor" strokeWidth={0} />
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default AnnouncementVideo;
